<template>
  <div class="step-schedule">
    <!-- Ongoing campaigns run every day: only the time and an optional stop
         date are chosen; the send modes below are for one time campaigns. -->
    <template v-if="ongoing">
      <div class="row">
        <div class="col-lg-5 col-md-6">
          <label>Send when</label>
          <base-input>
            <el-select :value="'birthday_in_days'" class="select-primary step-schedule__full">
              <el-option value="birthday_in_days" label="Birthday is a number of days away" class="select-primary" />
            </el-select>
          </base-input>
        </div>
        <div class="col-lg-3 col-md-4 col-sm-6">
          <label>Days before the birthday</label>
          <div class="step-schedule__pair">
            <base-input class="step-schedule__pair-days">
              <el-input-number :value="conditionDays" :controls="false" :min="0" :max="365" :precision="0" placeholder="7" @input="$emit('update:condition-days', $event)" />
            </base-input>
            <label>days</label>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-3 col-md-4 col-sm-6">
          <label>Sends every day at</label>
          <base-input>
            <time-picker
              :value="scheduleTime"
              :format="pickerTimeFormat"
              value-format="HH:mm"
              placeholder="Pick a time"
              class="step-schedule__full"
              @input="$emit('update:schedule-time', $event)"
            />
          </base-input>
        </div>
        <div class="col-lg-3 col-md-4 col-sm-6">
          <label>Stop after (optional)</label>
          <base-input>
            <el-date-picker
              :value="endsOn"
              type="date"
              :format="pickerDateFormat"
              value-format="yyyy-MM-dd"
              :picker-options="{ firstDayOfWeek: firstDayOfWeek, disabledDate: isPastDate }"
              placeholder="Runs until stopped"
              clearable
              class="step-schedule__full"
              @input="$emit('update:ends-on', $event || '')"
            />
          </base-input>
        </div>
      </div>

      <info-hint variant="primary" class="mb-0">
        Runs every day at this time in your group's timezone and emails the customers whose birthday is {{ conditionDaysLabel }} away that day, one email each. Customers without a birthday on file are never included. Leave Stop after empty to keep it running until you stop it.
      </info-hint>
      <info-hint v-if="quietHoursWarning" variant="warning" style="width: 100%" class="mt-2">
        {{ quietHoursWarning }}
      </info-hint>
    </template>

    <template v-else>
    <label class="d-block">When to send</label>
    <segmented-toggle
      :value="sendMode"
      variant="pills"
      :options="[
        { value: 'now', label: 'Send now' },
        { value: 'schedule', label: 'Schedule for later' }
      ]"
      @input="$emit('update:send-mode', $event)"
    />

    <info-hint v-if="sendMode === 'now'" style="width: 100%" class="mt-4">
      The campaign will start sending as soon as you confirm below.
    </info-hint>

    <div v-else class="mt-4">
      <div class="row">
        <div class="col-lg-3 col-md-4 col-sm-6">
          <label>Date</label>
          <base-input>
            <el-date-picker
              :value="scheduleDate"
              type="date"
              :format="pickerDateFormat"
              value-format="yyyy-MM-dd"
              :picker-options="{ firstDayOfWeek: firstDayOfWeek, disabledDate: isPastDate }"
              placeholder="Pick a date"
              class="step-schedule__full"
              @input="$emit('update:schedule-date', $event)"
            />
          </base-input>
        </div>
        <div class="col-lg-3 col-md-4 col-sm-6">
          <label>Time</label>
          <base-input>
            <time-picker
              :value="scheduleTime"
              :format="pickerTimeFormat"
              value-format="HH:mm"
              placeholder="Pick a time"
              class="step-schedule__full"
              @input="$emit('update:schedule-time', $event)"
            />
          </base-input>
        </div>
      </div>

      <p class="mb-2 step-schedule__muted"><small>Times are in your group's timezone ({{ tzName }}).</small></p>

      <info-hint v-if="resolvedDisplay" variant="primary" style="width: 100%">
        Will send <strong>{{ resolvedDisplay }}</strong>.
      </info-hint>
      <info-hint v-if="quietHoursWarning" variant="warning" style="width: 100%" class="mt-2">
        {{ quietHoursWarning }}
      </info-hint>
      <p v-if="pastError" class="text-danger mb-0"><small>{{ pastError }}</small></p>
    </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { TimePicker } from 'element-ui'
import moment from 'moment-timezone'
import { pickerDateFormat as getPickerDateFormat, pickerTimeFormat as getPickerTimeFormat, formatGroupDate, formatGroupTime } from '@/plugins/dates'

const props = defineProps({
  // true for an ongoing campaign: daily time plus an optional stop date
  ongoing: { type: Boolean, default: false },
  sendMode: { type: String, default: 'now' },
  scheduleDate: { type: String, default: '' }, // yyyy-MM-dd (group wall date)
  scheduleTime: { type: String, default: '' }, // HH:mm (group wall time)
  endsOn: { type: String, default: '' },       // yyyy-MM-dd, ongoing only
  conditionDays: { type: Number, default: 7 }  // ongoing only: days before the birthday
})

defineEmits(['update:send-mode', 'update:schedule-date', 'update:schedule-time', 'update:ends-on', 'update:condition-days'])

const conditionDaysLabel = computed(() => {
  const d = Number.isInteger(props.conditionDays) ? props.conditionDays : 0
  return d === 0 ? '0 days' : d === 1 ? '1 day' : `${d} days`
})

const group = JSON.parse(localStorage.getItem('group') || '{}')
const tzName = group.tz_name || 'UTC'
const firstDayOfWeek = Number(group.first_day_of_week) || 1
// The picker helpers take the group's stored format and translate it to
// element-ui tokens; called bare they silently fall back to US defaults.
// dddd prefixes the weekday name when the group shows day of week.
const pickerDateFormat = (group.show_day_of_week ? 'dddd, ' : '') + getPickerDateFormat(group.date_format)
const pickerTimeFormat = getPickerTimeFormat(group.time_format)

function isPastDate(date: Date) {
  const startOfToday = moment.tz(tzName).startOf('day')
  return moment(date).isBefore(startOfToday)
}

const scheduledMoment = computed(() => {
  if (!props.scheduleDate || !props.scheduleTime) return null
  return moment.tz(`${props.scheduleDate} ${props.scheduleTime}`, 'YYYY-MM-DD HH:mm', tzName)
})

const resolvedDisplay = computed(() => {
  const m = scheduledMoment.value
  if (!m || !m.isValid() || !m.isAfter(moment())) return ''
  const iso = m.toISOString()
  return `${formatGroupDate(iso)}, ${formatGroupTime(iso)}`
})

const quietHoursWarning = computed(() => {
  if ((!props.ongoing && props.sendMode !== 'schedule') || !props.scheduleTime) return ''
  const hour = Number(props.scheduleTime.split(':')[0])
  if (hour < 8 || hour >= 20) {
    return 'This send time falls outside typical waking hours. Marketing emails land best between 8:00 and 20:00 in your local timezone.'
  }
  return ''
})

const pastError = computed(() => {
  const m = scheduledMoment.value
  if (m && m.isValid() && !m.isAfter(moment())) {
    return 'The scheduled time must be in the future.'
  }
  return ''
})
</script>

<script lang="ts">
export default { name: 'campaign-step-schedule' }
</script>

<style lang="scss" scoped>
// Layout only; pickers, hints, and labels are global primitives.
.step-schedule {
  &__full {
    width: 100%;
  }

  &__muted {
    color: rgba(255, 255, 255, 0.75);
  }

  // layout only: number input beside its unit, same shape as the filter panel's pairs
  &__pair {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__pair-days {
    width: 90px;
    flex-shrink: 0;
  }
}

.white-content .step-schedule__muted {
  color: rgba(0, 0, 0, 0.7);
}
</style>
